import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ListPlus, Trash2, ShoppingBag, Package } from 'lucide-react';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import { productAPI, IMAGE_BASE } from '../../services/api';

const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

export default function Wishlist() {
    const { wishlistIds, toggleWishlist, clearWishlist } = useWishlist();
    const { addItem } = useCart();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProducts = async () => {
            if (wishlistIds.size === 0) {
                setProducts([]);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const res = await productAPI.getAll({ limit: 100 });
                const allProducts = res.data.products || res.data || [];
                const filtered = allProducts.filter(p => wishlistIds.has(p.id));
                setProducts(filtered);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        fetchProducts();
    }, [wishlistIds]);

    const getImageUrl = (product) => {
        const img = product.images?.[0];
        if (!img) return null;
        return img.url?.startsWith('http') ? img.url : IMAGE_BASE + img.url;
    };

    return (
        <div className="wishlist-page">
            <div className="wishlist-header">
                <h1><ListPlus size={28} /> İstek Listem</h1>
                {products.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span className="wishlist-count">{products.length} ürün</span>
                        <button className="wishlist-clear-btn" onClick={clearWishlist}>
                            <Trash2 size={14} /> Listeyi Temizle
                        </button>
                    </div>
                )}
            </div>

            {loading ? (
                <div className="loading"><div className="spinner"></div></div>
            ) : products.length === 0 ? (
                <div className="wishlist-empty">
                    <ListPlus size={48} strokeWidth={1} />
                    <h2>İstek listeniz boş</h2>
                    <p>Beğendiğiniz ürünleri istek listesine ekleyerek daha sonra kolayca ulaşabilirsiniz.</p>
                    <Link to="/urunler" className="favorites-login-btn">Ürünleri Keşfet</Link>
                </div>
            ) : (
                <div className="wishlist-grid">
                    {products.map(product => {
                        const imgUrl = getImageUrl(product);
                        const price = parseFloat(product.price || 0);
                        const comparePrice = product.comparePrice ? parseFloat(product.comparePrice) : null;
                        const discount = comparePrice ? Math.round((1 - price / comparePrice) * 100) : 0;
                        return (
                            <div key={product.id} className="wishlist-card">
                                <Link to={`/urun/${product.slug}`} className="wishlist-card-img">
                                    {imgUrl ? (
                                        <img src={imgUrl} alt={product.name} loading="lazy" />
                                    ) : (
                                        <Package size={40} strokeWidth={1} />
                                    )}
                                    {discount > 0 && <span className="wishlist-discount">%{discount}</span>}
                                </Link>
                                <div className="wishlist-card-info">
                                    <Link to={`/urun/${product.slug}`}>
                                        <h3>{product.name}</h3>
                                    </Link>
                                    <div className="wishlist-card-price">
                                        <span className="current">{formatPrice(price)}</span>
                                        {comparePrice && <span className="compare">{formatPrice(comparePrice)}</span>}
                                    </div>
                                    <div className="wishlist-card-actions">
                                        <button
                                            className="wishlist-add-cart"
                                            onClick={() => addItem(product.id, null, 1)}
                                            disabled={product.stock <= 0}
                                        >
                                            <ShoppingBag size={15} />
                                            {product.stock > 0 ? 'Sepete Ekle' : 'Stokta Yok'}
                                        </button>
                                        <button
                                            className="wishlist-remove"
                                            onClick={() => toggleWishlist(product.id)}
                                            title="Listeden Kaldır"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
